import { CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { useInfiniteEstimateList } from '../../../services/business/estimates/estimates.hooks';
import { formatCurrency } from '../../../utility/helpers/displayHelper';
import TabCards from './TabCards';
import SearchTextfeild from '../../../utility/Svg/SearchTextfeild';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import useDebounce from '../../../utility/useDebounce';
import { getEstimateDetailUrl } from '../../../common/LinkedDocumentLink';

const EstimateTab = ({ customerId, vehicleId }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    const queryParams = {
        limit: 20,
        status: 'all',
        sortType: 'DSC',
        sortBy: 'createdAt',
        customer: customerId,
        customerSearch: debouncedSearch,
    };

    if (vehicleId) {
        queryParams.vehicleId = vehicleId;
    }

    const {
        data: estimateQueryData,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteEstimateList(queryParams, {
        enabled: !!customerId,
        refetchOnMount: true,
    });

    // Flatten all pages into a single array
    const estimateList = useMemo(() => {
        if (!estimateQueryData?.pages) return [];
        return estimateQueryData.pages.flatMap((page) => {
            // Handle different API response structures
            if (Array.isArray(page)) {
                return page;
            }
            return page?.data || [];
        });
    }, [estimateQueryData]);

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    return (
        <Grid container spacing={3}>
            {/* Searchbar  */}
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search estimate, licence plate or unit number"
                    sx={{ ...MuiTextFieldWithFocus, background: '#fff' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchTextfeild />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>

            {isLoading && estimateList.length === 0 && (
                <Grid item xs={12} container justifyContent="center" alignItems="center" style={{ height: 350 }}>
                    <CircularProgress />
                </Grid>
            )}

            {!isLoading && estimateList.length === 0 && (
                <Grid item xs={12} container justifyContent="center" alignItems="center" style={{ height: 350 }}>
                    No Data Found
                </Grid>
            )}

            {estimateList.length > 0 && (
                <Grid item xs={12}>
                    <InfiniteScroll
                        dataLength={estimateList.length}
                        next={handleLoadMore}
                        hasMore={hasNextPage || false}
                        loader={
                            isFetchingNextPage && (
                                <Grid container justifyContent="center" alignItems="center" style={{ padding: '20px 0' }}>
                                    <CircularProgress size={24} />
                                </Grid>
                            )
                        }
                        endMessage={
                            !hasNextPage &&
                            estimateList.length > 0 && (
                                <Grid container justifyContent="center" alignItems="center" style={{ padding: '20px 0' }}>
                                    {/* <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>No more estimates to load</p> */}
                                </Grid>
                            )
                        }
                    >
                        <Grid container spacing={3}>
                            {estimateList.map((invoice) => {
                                const { _id, Total, createdAt, estimateNumber, vehicle, note, serviceCount, services } = invoice;

                                const navigateUrl = getEstimateDetailUrl(invoice);

                                const vehicleString =
                                    `${vehicle?.year || ''} ${vehicle?.vehicleMake?.name || ''} ${vehicle?.vehicleModel?.name || ''} ${
                                        vehicle?.vehicleModelTrim?.name || ''
                                    }`.trim() || '';

                                const province = `${vehicle?.provinceCode || ''} ${vehicle?.licenseNumber || ''}`.trim() || '';
                                const fleetUnitNumber = vehicle?.fleetUnitNumber || '';
                                const vin = vehicle?.vin || '';

                                return (
                                    <Grid item xs={12} key={_id}>
                                        <TabCards
                                            amount={formatCurrency(Total)}
                                            invoiceNumber={estimateNumber}
                                            date={dayjs(createdAt).format('MMMM DD, YYYY')}
                                            vehicle={vehicleString}
                                            province={province}
                                            vin={vin}
                                            fleetUnitNumber={fleetUnitNumber}
                                            totalService={
                                                !services.length > 0
                                                    ? ''
                                                    : serviceCount > 1
                                                      ? `${serviceCount} Services`
                                                      : `${(services?.length > 0 && (services[0]?.name || services[0]?.serviceName)) || undefined}`
                                            }
                                            navigate={navigate}
                                            navigateUrl={navigateUrl}
                                            note={note}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </InfiniteScroll>
                </Grid>
            )}
        </Grid>
    );
};

export default EstimateTab;
